import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Row,
  Col,
  Popconfirm,
  Avatar,
  Modal,
  Tooltip,
  Tabs,
  InputNumber,
  Form,
  theme,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { employeeService } from '../../services/employeeService';
import { departmentService } from '../../services/departmentService';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import EmployeeForm from '../../components/employee/EmployeeForm';
import notify from '../../utils/notify';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import formatDateRo from '../../utils/formatDateRo';
import HolidayTable from '../../components/holiday/HolidayTable';
import ViewModeSelect from '../../components/common/ViewModeSelect';
import useResponsiveTableView from '../../hooks/useResponsiveTableView';

const { Title, Text } = Typography;
const { Search } = Input;
const { useToken } = theme;

const NEGATIVE_LEAVE_BALANCE_CODE = 'NEGATIVE_LEAVE_BALANCE';

const LEAVE_TYPE_COLORS = {
  VACATION: 'blue',
  SICK: 'red',
  MATERNITY: 'pink',
  PATERNITY: 'pink',
  BEREAVEMENT: 'black',
  MARRIAGE: 'purple',
};

const LEAVE_TYPE_ORDER = {
  VACATION: 0,
  SICK: 1,
  BEREAVEMENT: 2,
  MATERNITY: 3,
  PATERNITY: 4,
  MARRIAGE: 5,
};

const CURRENT_YEAR = new Date().getFullYear();

const EMPLOYEE_TYPE_EMPLOYEE = 'EMPLOYEE';
const EMPLOYEE_TYPE_COLLABORATOR = 'COLLABORATOR';
// Below this container width the 7-column table can't be shown in full → cards.
// Tuned so a standard desktop (≥ ~1150px viewport, content ~900px after the sidebar)
// still shows the table, while tablets/phones fall back to cards.
const MIN_TABLE_WIDTH = 850;

const EmployeeList = () => {
  const { canManageEmployees, isHR } = useAuth();
  const { token } = useToken();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  // Holds the projected-negative-balance conflict (and the pending form payload) returned by a
  // 409 from the hire-date change, so the editor can acknowledge it before the update is retried.
  const [negativeBalanceConflict, setNegativeBalanceConflict] = useState(null);
  const [searchText, setSearchText] = useState('');
  // Pre-seed the department filter from the URL (?department=<id>) so that
  // navigating here from a department card lands on a filtered list.
  const [selectedDepartment, setSelectedDepartment] = useState(searchParams.get('department'));
  const [selectedEmployeeType, setSelectedEmployeeType] = useState(null);
  const [manualView, setManualView] = useState('auto');
  const { containerRef, view } = useResponsiveTableView({ minTableWidth: MIN_TABLE_WIDTH, manualView });
  const canModifyEmployee = (employee) =>
    canManageEmployees() && !(isHR?.() && employee.role === 'ADMIN');

  // Time-off state
  const [expandedBalances, setExpandedBalances] = useState({});
  const [balancesLoading, setBalancesLoading] = useState({});
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustingBalance, setAdjustingBalance] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [timeOffModalEmployee, setTimeOffModalEmployee] = useState(null);
  const [adjustForm] = Form.useForm();

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      notify.error(t('employee.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAll();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchBalances = async (employeeId) => {
    setBalancesLoading((prev) => ({ ...prev, [employeeId]: true }));
    try {
      const data = await leaveService.getEmployeeBalances(employeeId);
      const currentYearBalances = data
        .filter((b) => b.year === CURRENT_YEAR)
        .sort((a, b) => (LEAVE_TYPE_ORDER[a.leaveType] ?? 99) - (LEAVE_TYPE_ORDER[b.leaveType] ?? 99));
      setExpandedBalances((prev) => ({ ...prev, [employeeId]: currentYearBalances }));
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      notify.error(t('employee.balance.fetchError'));
    } finally {
      setBalancesLoading((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setFormOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setFormOpen(true);
  };

  const handleDeleteEmployee = async (employee) => {
    try {
      await employeeService.delete(employee.id);
      notify.success(t('employee.deleteSuccess'));
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      notify.error(getApiErrorMessage(error, 'employee.fetchError', t));
    }
  };

  const submitEmployee = async (formData) => {
    if (editingEmployee) {
      await employeeService.update(editingEmployee.id, formData);
      notify.success(t('employee.updateSuccess'));
    } else {
      await employeeService.create(formData);
      notify.success(t('employee.createSuccess'));
    }
    setFormOpen(false);
    fetchEmployees();
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      await submitEmployee(formData);
    } catch (error) {
      const conflict = error.response?.data;
      // A hire-date change that drives the vacation balance negative comes back as a 409
      // the editor must explicitly acknowledge before it is applied.
      if (editingEmployee && conflict?.code === NEGATIVE_LEAVE_BALANCE_CODE) {
        setNegativeBalanceConflict({ ...conflict, formData });
        return;
      }
      console.error('Error saving employee:', error);
      notify.error(getApiErrorMessage(error, 'employee.saveError', t));
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmNegativeBalance = async () => {
    const { formData } = negativeBalanceConflict;
    setNegativeBalanceConflict(null);
    setFormLoading(true);
    try {
      await submitEmployee({ ...formData, acknowledgeNegativeLeaveBalance: true });
    } catch (error) {
      console.error('Error saving employee:', error);
      notify.error(getApiErrorMessage(error, 'employee.saveError', t));
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenAdjust = (balance) => {
    setAdjustingBalance(balance);
    adjustForm.setFieldsValue({ balance: balance.balance });
    setAdjustModalOpen(true);
  };

  const handleAdjustSubmit = async () => {
    setAdjustLoading(true);
    try {
      const { balance } = await adjustForm.validateFields();
      await leaveService.adjustBalance({
        employeeId: adjustingBalance.employeeId,
        leaveType: adjustingBalance.leaveType,
        balance,
        year: adjustingBalance.year,
      });
      notify.success(t('employee.balance.updateSuccess'));
      setAdjustModalOpen(false);
      // Refresh the expanded row for that employee
      await fetchBalances(adjustingBalance.employeeId);
    } catch (error) {
      if (error?.errorFields) return; // form validation error, don't show message
      console.error('Error adjusting balance:', error);
      notify.error(getApiErrorMessage(error, 'employee.balance.updateError', t));
    } finally {
      setAdjustLoading(false);
    }
  };

  const handleOpenTimeOffModal = async (employee) => {
    setTimeOffModalEmployee(employee);
    if (!expandedBalances[employee.id]) {
      await fetchBalances(employee.id);
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch = !searchText ||
      employee.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchText.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchText.toLowerCase());

    const matchesDepartment = !selectedDepartment ||
      employee.departmentId?.toString() === selectedDepartment;

    const matchesEmployeeType = !selectedEmployeeType ||
      employee.employeeType === selectedEmployeeType;

    return matchesSearch && matchesDepartment && matchesEmployeeType;
  });

  const hasActiveFilters = !!searchText || !!selectedDepartment || !!selectedEmployeeType;

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedDepartment(null);
    setSelectedEmployeeType(null);
  };

  const balanceColumns = [
    {
      title: t('employee.balance.leaveType'),
      dataIndex: 'leaveType',
      key: 'leaveType',
      render: (type) => (
        <Tag color={LEAVE_TYPE_COLORS[type] || 'default'}>
          {t(`leave.form.types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('employee.balance.remainingDays'),
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => (
        <Text strong>{parseFloat(balance).toFixed(1)}</Text>
      ),
    },
    {
      title: t('employee.balance.year'),
      dataIndex: 'year',
      key: 'year',
    },
    canManageEmployees() && {
      title: t('employee.balance.actions'),
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          className="touch-button"
          icon={<EditOutlined />}
          onClick={() => handleOpenAdjust(record)}
          data-testid="adjust-balance-button"
        >
          {t('employee.balance.adjust')}
        </Button>
      ),
    },
  ].filter(Boolean);

  const expandedRowRender = (record) => (
    <Table
      columns={balanceColumns}
      dataSource={expandedBalances[record.id] || []}
      rowKey="id"
      loading={balancesLoading[record.id]}
      pagination={false}
      size="small"
      locale={{ emptyText: t('employee.balance.noBalances') }}
      data-testid="time-off-balances-table"
    />
  );

  const columns = [
    {
      title: t('employee.columns.employee'),
      key: 'employee',
      render: (_, record) => (
        <Space>
          <Avatar
            style={{
              backgroundColor: record.departmentColor || token.colorPrimary,
            }}
            icon={<UserOutlined />}
          />
          <div>
            <Text strong>{`${record.firstName} ${record.lastName}`}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.position}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: t('employee.columns.department'),
      dataIndex: 'departmentName',
      key: 'department',
      render: (text, record) => record.departmentColor ? (
        <Tag
          style={{
            backgroundColor: record.departmentColor,
            color: '#fff',
            border: 'none',
          }}
        >
          {text}
        </Tag>
      ) : (
        <Tag>{text || '-'}</Tag>
      ),
    },
    {
      title: t('employee.columns.email'),
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space>
          <MailOutlined style={{ color: token.colorTextSecondary }} />
          <Text copyable>{email}</Text>
        </Space>
      ),
    },
    {
      title: t('employee.columns.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone ? (
        <Space>
          <PhoneOutlined style={{ color: token.colorTextSecondary }} />
          {phone}
        </Space>
      ) : '-',
    },
    {
      title: t('employee.columns.employeeType'),
      dataIndex: 'employeeType',
      key: 'employeeType',
      render: (type) => (
        <Tag color={type === 'COLLABORATOR' ? 'orange' : 'blue'}>
          {type === 'COLLABORATOR'
            ? t('employee.form.employeeTypeCollaborator')
            : t('employee.form.employeeTypeEmployee')}
        </Tag>
      ),
    },
    {
      title: t('employee.columns.hireDate'),
      dataIndex: 'hireDate',
      key: 'hireDate',
      render: (date) => (
        <Space>
          <CalendarOutlined style={{ color: token.colorTextSecondary }} />
          {formatDateRo(date)}
        </Space>
      ),
    },
    {
      title: t('employee.columns.actions'),
      key: 'actions',
      fixed: view === 'table' ? 'right' : undefined,
      render: (_, record) => canModifyEmployee(record) && (
        <Space data-testid="employee-actions">
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditEmployee(record)}
              data-testid="employee-action-edit"
            />
          </Tooltip>
          <Popconfirm
            title={t('employee.deleteTitle')}
            description={t('employee.deleteConfirm', { name: `${record.firstName} ${record.lastName}` })}
            onConfirm={() => handleDeleteEmployee(record)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                data-testid="employee-action-delete"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const EmployeeCard = ({ employee }) => (
    <Card
      hoverable
      className="employee-card"
      actions={canModifyEmployee(employee) ? [
        <EditOutlined key="edit" onClick={() => handleEditEmployee(employee)} />,
        <ClockCircleOutlined
          key="time-off"
          title={t('employee.balance.viewTimeOff')}
          onClick={() => handleOpenTimeOffModal(employee)}
          data-testid="card-time-off-button"
        />,
        <Popconfirm
          title={t('employee.deleteTitle')}
          description={t('employee.deleteConfirm', { name: `${employee.firstName} ${employee.lastName}` })}
          onConfirm={() => handleDeleteEmployee(employee)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <DeleteOutlined key="delete" />
        </Popconfirm>,
      ] : [
        <ClockCircleOutlined
          key="time-off"
          title={t('employee.balance.viewTimeOff')}
          onClick={() => handleOpenTimeOffModal(employee)}
          data-testid="card-time-off-button"
        />,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="start">
          <Avatar
            size={48}
            style={{
              backgroundColor: employee.departmentColor || token.colorPrimary,
            }}
            icon={<UserOutlined />}
          />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {`${employee.firstName} ${employee.lastName}`}
            </Title>
            <Text type="secondary">{employee.position}</Text>
          </div>
        </Space>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <MailOutlined style={{ color: token.colorTextSecondary }} />
            <Text copyable>{employee.email}</Text>
          </Space>
          {employee.phone && (
            <Space>
              <PhoneOutlined style={{ color: token.colorTextSecondary }} />
              <Text>{employee.phone}</Text>
            </Space>
          )}
          <Space>
            <CalendarOutlined style={{ color: token.colorTextSecondary }} />
            <Text>{t('employee.hiredOn', { date: new Date(employee.hireDate).toLocaleDateString() })}</Text>
          </Space>
        </Space>

        <Space wrap>
          <Tag
            color={employee.departmentColor}
            style={{
              borderColor: employee.departmentColor,
              backgroundColor: `${employee.departmentColor}20`,
              color: employee.departmentColor,
            }}
          >
            {employee.departmentName}
          </Tag>
        </Space>
      </Space>
    </Card>
  );

  const employeesContent = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div />
        {canManageEmployees() && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddEmployee}
            size="large"
            data-testid="add-employee-button"
          >
            {t('employee.addEmployee')}
          </Button>
        )}
      </div>

      <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder={t('employee.searchPlaceholder')}
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder={t('employee.filterDepartment')}
              allowClear
              style={{ width: '100%' }}
              size="large"
              onChange={setSelectedDepartment}
              value={selectedDepartment}
            >
              {departments.map((dept) => (
                <Select.Option key={dept.id} value={dept.id.toString()}>
                  <Space>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: dept.colorHex,
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                    {dept.name}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder={t('employee.filterEmployeeType')}
              allowClear
              style={{ width: '100%' }}
              size="large"
              onChange={setSelectedEmployeeType}
              value={selectedEmployeeType}
              data-testid="employee-type-filter"
            >
              <Select.Option value={EMPLOYEE_TYPE_EMPLOYEE}>
                {t('employee.form.employeeTypeEmployee')}
              </Select.Option>
              <Select.Option value={EMPLOYEE_TYPE_COLLABORATOR}>
                {t('employee.form.employeeTypeCollaborator')}
              </Select.Option>
            </Select>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
          }}
        >
          <Button
            icon={<ClearOutlined />}
            size="large"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            data-testid="clear-filters-button"
          >
            {t('employee.clearFilters')}
          </Button>
          <ViewModeSelect value={manualView} onChange={setManualView} data-testid="view-mode-select" />
        </div>
      </Card>

      <div ref={containerRef}>
        {view === 'table' ? (
          <Card>
            <Table
              columns={columns}
              dataSource={filteredEmployees}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => t('employee.total', { total }),
              }}
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowRender,
                onExpand: (expanded, record) => {
                  if (expanded && !expandedBalances[record.id]) {
                    fetchBalances(record.id);
                  }
                },
              }}
            />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredEmployees.map((employee, index) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={employee.id}>
                <div style={{ animationDelay: `${index * 30}ms` }} className="animate-fade-in-up">
                  <EmployeeCard employee={employee} />
                </div>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </Space>
  );

  const tabItems = [
    {
      key: 'employees',
      label: t('employee.tabEmployees'),
      children: employeesContent,
    },
    {
      key: 'holidays',
      label: t('employee.tabBankHolidays'),
      children: <HolidayTable />,
    },
  ];

  return (
    <div className="employee-list">
      <Title level={2}>{t('employee.title')}</Title>
      <Tabs defaultActiveKey="employees" items={tabItems} size="large" />

      {/* Add/Edit Employee Modal */}
      <Modal
        title={editingEmployee ? t('employee.editEmployee') : t('employee.addEmployee')}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        footer={null}
        width="min(720px, 95vw)"
        destroyOnClose
      >
        <EmployeeForm
          onSubmit={handleFormSubmit}
          employee={editingEmployee}
          loading={formLoading}
          departments={departments}
        />
      </Modal>

      {/* Negative leave-balance acknowledgment Modal */}
      <Modal
        title={t('employee.negativeBalanceWarning.title')}
        open={!!negativeBalanceConflict}
        onOk={handleConfirmNegativeBalance}
        onCancel={() => setNegativeBalanceConflict(null)}
        okText={t('employee.negativeBalanceWarning.proceed')}
        cancelText={t('employee.negativeBalanceWarning.cancel')}
        okButtonProps={{ danger: true, 'data-testid': 'confirm-negative-balance' }}
        confirmLoading={formLoading}
        destroyOnClose
      >
        {negativeBalanceConflict && (
          <Text>
            {t('employee.negativeBalanceWarning.message', {
              year: negativeBalanceConflict.year,
              days: Math.abs(Number(negativeBalanceConflict.projectedNet)),
            })}
          </Text>
        )}
      </Modal>

      {/* Adjust Balance Modal */}
      <Modal
        title={
          adjustingBalance
            ? t('employee.balance.adjustTitle', {
                leaveType: t(`leave.form.types.${adjustingBalance.leaveType}`),
                name: adjustingBalance.employeeName,
              })
            : t('employee.balance.adjustTitleFallback')
        }
        open={adjustModalOpen}
        onCancel={() => setAdjustModalOpen(false)}
        onOk={handleAdjustSubmit}
        okText={t('common.save')}
        confirmLoading={adjustLoading}
        destroyOnClose
        data-testid="adjust-balance-modal"
      >
        {adjustingBalance && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              {t('employee.balance.currentBalance')} <Text strong>{parseFloat(adjustingBalance.balance).toFixed(1)} {t('employee.balance.days')}</Text>
            </Text>
            <Form form={adjustForm} layout="vertical">
              <Form.Item
                name="balance"
                label={t('employee.balance.newBalance')}
                rules={[
                  { required: true, message: t('employee.balance.balanceRequired') },
                  {
                    validator: (_, value) =>
                      value >= 0
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('employee.balance.balanceNegative'))),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.5}
                  precision={1}
                  style={{ width: '100%' }}
                  autoFocus
                  data-testid="balance-input"
                />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>

      {/* Card view: Time Off Modal */}
      <Modal
        title={
          timeOffModalEmployee
            ? t('employee.balance.timeOffTitle', {
                name: `${timeOffModalEmployee.firstName} ${timeOffModalEmployee.lastName}`,
                year: CURRENT_YEAR,
              })
            : t('employee.balance.timeOffFallback')
        }
        open={!!timeOffModalEmployee}
        onCancel={() => setTimeOffModalEmployee(null)}
        footer={null}
        width="min(600px, 95vw)"
        destroyOnClose
        data-testid="time-off-modal"
      >
        {timeOffModalEmployee && (
          <Table
            columns={balanceColumns}
            dataSource={expandedBalances[timeOffModalEmployee.id] || []}
            rowKey="id"
            loading={balancesLoading[timeOffModalEmployee.id]}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('employee.balance.noBalances') }}
          />
        )}
      </Modal>
    </div>
  );
};

export default EmployeeList;
